<!--
  필터링 키워드 관리 팝업
  - 보고서/통계 합계 산정 시 제외할 키워드(부자재·서비스 항목 등)를 운영자가 직접 추가/수정/삭제
  - 공통코드 그룹 T024 (upCd) 사용
  - 키워드는 cdNm 컬럼에 저장 — 백엔드 SQL은 NOT LIKE %cdNm% 으로 활용
-->
<template>
  <CModal :visible="visible" size="lg" backdrop="static" @close="closeModal">
    <CModalHeader>
      <CModalTitle>필터링 키워드 관리</CModalTitle>
    </CModalHeader>
    <CModalBody>
      <CAlert color="info" class="py-2 mb-3">
        보고서·통계 합계에서 <b>제외</b>할 키워드를 관리합니다.
        <br />상품명에 키워드가 포함되면 해당 행은 합계 산정에서 제외됩니다. (부분일치)
      </CAlert>

      <!-- 신규 등록 영역 -->
      <CRow class="g-2 mb-3">
        <CCol :xs="5">
          <CFormInput
            type="text"
            v-model="newKeyword.cdNm"
            placeholder="키워드 (예: 각인, 배송)"
            maxlength="50"
            @keyup.enter="insertKeyword"
          />
        </CCol>
        <CCol :xs="5">
          <CFormInput
            type="text"
            v-model="newKeyword.cdDc"
            placeholder="설명 (선택)"
            maxlength="200"
            @keyup.enter="insertKeyword"
          />
        </CCol>
        <CCol :xs="2" class="d-grid">
          <CButton color="dark" @click="insertKeyword" :disabled="loading">
            추가 +
          </CButton>
        </CCol>
      </CRow>

      <!-- 등록 키워드 목록 -->
      <CTable bordered hover small align="middle" class="mb-0">
        <CTableHead color="light">
          <CTableRow>
            <CTableHeaderCell scope="col" class="text-center" style="width: 60px">No</CTableHeaderCell>
            <CTableHeaderCell scope="col" class="text-center" style="width: 30%">키워드</CTableHeaderCell>
            <CTableHeaderCell scope="col">설명</CTableHeaderCell>
            <CTableHeaderCell scope="col" class="text-center" style="width: 100px">사용</CTableHeaderCell>
            <CTableHeaderCell scope="col" class="text-center" style="width: 100px">관리</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody v-if="keywords.length > 0">
          <CTableRow v-for="(item, idx) in keywords" :key="item.cd">
            <CTableDataCell class="text-center">{{ idx + 1 }}</CTableDataCell>
            <CTableDataCell>
              <CFormInput
                type="text"
                v-model="item.cdNm"
                size="sm"
                maxlength="50"
                @blur="updateKeywordIfChanged(item)"
              />
            </CTableDataCell>
            <CTableDataCell>
              <CFormInput
                type="text"
                v-model="item.cdDc"
                size="sm"
                maxlength="200"
                @blur="updateKeywordIfChanged(item)"
              />
            </CTableDataCell>
            <CTableDataCell class="text-center">
              <CFormSwitch
                size="sm"
                :checked="item.useSttus === '1'"
                @change="toggleUseSttus(item)"
              />
            </CTableDataCell>
            <CTableDataCell class="text-center">
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                @click="deleteKeyword(item)"
              >
                삭제
              </CButton>
            </CTableDataCell>
          </CTableRow>
        </CTableBody>
        <CTableBody v-else>
          <CTableRow>
            <CTableDataCell colspan="5" class="text-center text-muted">
              등록된 키워드가 없습니다. 위에서 추가해 주세요.
            </CTableDataCell>
          </CTableRow>
        </CTableBody>
      </CTable>
    </CModalBody>
    <CModalFooter>
      <CButton color="secondary" @click="closeModal">닫기</CButton>
    </CModalFooter>
  </CModal>
</template>

<script>
import mgrCdMngApi from '@/api/mgr/admin_mgrCdMng'

const FILTER_UP_CD = 'T024'
const MAX_CD_SEQ = 999
const INSERT_RETRY_LIMIT = 3

export default {
  name: 'FilterKeywordMngModal',
  props: {
    visible: { type: Boolean, required: true, default: false },
  },
  emits: ['close', 'updated'],
  data() {
    return {
      keywords: [],
      // cd → 원본 스냅샷 (변경감지용)
      keywordSnapshot: {},
      newKeyword: { cdNm: '', cdDc: '' },
      loading: false,
      changed: false,
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadKeywords()
        this.changed = false
      }
    },
  },
  methods: {
    loadKeywords() {
      this.loading = true
      mgrCdMngApi
        .listMgrCdMng({
          params: {
            page: 1,
            pageSize: 999,
            searchKey: 'upCd',
            searchValue: FILTER_UP_CD,
          },
        })
        .then((response) => {
          const data = response.data.data
          const rows = (data && data.content ? data.content : []).map((row) => ({
            ...row,
            cdNm: (row.cdNm || '').trim(),
            useSttus: String(row.useSttus),
            cdDc: row.cdDc || '',
          }))
          this.keywords = rows
          this.keywordSnapshot = rows.reduce((acc, r) => {
            acc[r.cd] = { cdNm: r.cdNm, cdDc: r.cdDc, useSttus: r.useSttus }
            return acc
          }, {})
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loading = false
        })
    },
    nextCd(usedCds) {
      const used = (usedCds || this.keywords.map((k) => k.cd))
        .filter((cd) => cd && cd.startsWith(FILTER_UP_CD))
        .map((cd) => parseInt(cd.replace(FILTER_UP_CD, ''), 10))
        .filter((n) => !isNaN(n))
      const next = used.length > 0 ? Math.max(...used) + 1 : 1
      if (next > MAX_CD_SEQ) return null
      return FILTER_UP_CD + String(next).padStart(3, '0')
    },
    insertKeyword() {
      const cdNm = (this.newKeyword.cdNm || '').trim()
      if (!cdNm) {
        alert('키워드를 입력하세요.')
        return
      }
      if (this.keywords.some((k) => k.cdNm === cdNm)) {
        alert('이미 등록된 키워드입니다.')
        return
      }
      this.tryInsert(cdNm, (this.newKeyword.cdDc || '').trim(), 0)
    },
    // 동시 등록으로 인한 PK 충돌(채번 race) 시 최신 목록을 다시 조회 후 재시도
    tryInsert(cdNm, cdDc, attempt) {
      const cd = this.nextCd()
      if (!cd) {
        alert(`키워드는 최대 ${MAX_CD_SEQ}개까지 등록 가능합니다.`)
        return
      }
      const payload = {
        upCd: FILTER_UP_CD,
        cd,
        cdNm,
        cdDc,
        useSttus: '1',
        sortSn: this.keywords.length + 1,
        frstRgtrSn: this.$store.state.mbrSn,
      }

      this.loading = true
      mgrCdMngApi
        .insertMgrCdMng({ params: payload })
        .then((response) => {
          if (response.data.statusCode == '1') {
            this.newKeyword = { cdNm: '', cdDc: '' }
            this.changed = true
            this.loadKeywords()
            return
          }
          // 실패 — 동시 등록으로 cd 중복일 가능성. 최신 목록 받고 재시도.
          if (attempt < INSERT_RETRY_LIMIT) {
            mgrCdMngApi
              .listMgrCdMng({
                params: { page: 1, pageSize: 999, searchKey: 'upCd', searchValue: FILTER_UP_CD },
              })
              .then((res) => {
                const data = res.data.data
                this.keywords = (data && data.content ? data.content : []).map((row) => ({
                  ...row,
                  useSttus: String(row.useSttus),
                  cdDc: row.cdDc || '',
                }))
                this.tryInsert(cdNm, cdDc, attempt + 1)
              })
              .catch((error) => {
          console.log(error)
          alert('통신 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.')
        })
            return
          }
          alert(response.data.statusMsg || '등록 실패')
        })
        .catch((error) => {
          console.log(error)
          alert('통신 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    isChanged(item) {
      const snap = this.keywordSnapshot[item.cd]
      if (!snap) return true
      return (
        snap.cdNm !== (item.cdNm || '').trim() ||
        snap.cdDc !== (item.cdDc || '') ||
        snap.useSttus !== item.useSttus
      )
    },
    updateKeywordIfChanged(item) {
      if (!this.isChanged(item)) return
      this.updateKeyword(item)
    },
    updateKeyword(item) {
      const cdNm = (item.cdNm || '').trim()
      if (!cdNm) {
        alert('키워드는 빈 값일 수 없습니다.')
        this.loadKeywords()
        return
      }
      const payload = {
        upCd: item.upCd,
        cd: item.cd,
        cdNm,
        cdDc: item.cdDc || '',
        useSttus: item.useSttus,
        sortSn: item.sortSn,
        lastMdfrSn: this.$store.state.mbrSn,
      }
      mgrCdMngApi
        .updateMgrCdMng({ params: payload })
        .then((response) => {
          if (response.data.statusCode == '1') {
            this.changed = true
            // 스냅샷 갱신 — 동일 row 재편집 시 불필요한 PUT 방지
            this.keywordSnapshot[item.cd] = {
              cdNm,
              cdDc: payload.cdDc,
              useSttus: payload.useSttus,
            }
          } else {
            alert(response.data.statusMsg || '수정 실패')
            this.loadKeywords()
          }
        })
        .catch((error) => {
          console.log(error)
          alert('통신 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.')
        })
    },
    toggleUseSttus(item) {
      item.useSttus = item.useSttus === '1' ? '0' : '1'
      this.updateKeyword(item)
    },
    deleteKeyword(item) {
      if (!confirm(`'${item.cdNm}' 키워드를 삭제하시겠습니까?`)) return
      mgrCdMngApi
        .deleteMgrCdMng({ params: { upCd: item.upCd, cd: item.cd } })
        .then((response) => {
          if (response.data.statusCode == '1') {
            this.changed = true
            this.loadKeywords()
          } else {
            alert(response.data.statusMsg || '삭제 실패')
          }
        })
        .catch((error) => {
          console.log(error)
          alert('통신 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.')
        })
    },
    closeModal() {
      if (this.changed) {
        this.$emit('updated')
      }
      this.$emit('close')
    },
  },
}
</script>
